import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import classNames from 'classnames';

import { Overlay } from '@picsart/design-system/components/Overlay';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconArrowLeft } from '@picsart/design-system/foundation/icons/IconArrowLeft';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconSoundOff } from '@picsart/design-system/foundation/icons/IconSoundOff';
import { IconSoundOn } from '@picsart/design-system/foundation/icons/IconSoundOn';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { Skeleton } from '@picsart/design-system/components/Skeleton';
import { useAppStore, type IAppStore } from '@/store';
import { Screen } from '@/types';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';
import usePresetFeedData from '@/hooks/usePresetFeedData';
import usePresetFeedActions from '@/hooks/usePresetFeedActions';
import usePresetFeedAnalytics from '@/hooks/usePresetFeedAnalytics';
import useFeedAudio from '@/hooks/useFeedAudio';
import useFeedPaging from '@/hooks/useFeedPaging';
import useAuth from '@/hooks/useAuth';
import useHideBackButton from '@/hooks/useHideBackButton';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import {
  activatePriorityVideo,
  deactivatePriorityVideo,
} from '@/utils/media/videoPauseCoordinator';
import FeedInputsSheet from '../FeedInputsSheet';
import FeedScrollHint from '../FeedScrollHint';
import FeedSlideList from '../FeedSlideList';
import useStyles from './styles';

const selector = (store: IAppStore) => ({
  setScreen: store.setScreen,
  setPresetFeedEntrySlug: store.setPresetFeedEntrySlug,
  setFeedReturnSlug: store.setFeedReturnSlug,
  hasSeenFeedScrollHint: store.hasSeenFeedScrollHint,
  markFeedScrollHintSeen: store.markFeedScrollHintSeen,
});

const PresetFeedScreen = () => {
  const classes = useStyles();
  const { items, initialIndex, isLoading } = usePresetFeedData();
  const {
    setScreen,
    setPresetFeedEntrySlug,
    setFeedReturnSlug,
    hasSeenFeedScrollHint,
    markFeedScrollHintSeen,
  } = useAppStore(selector);

  const {
    loadActivePreset,
    handleUploadTap,
    handleEditTap,
    isUploadLoading,
    isInputsSheetOpen,
    closeSheet,
    handleSheetGenerate,
    isGenerating,
    filePickerInputProps,
  } = usePresetFeedActions();

  const { scrollRef, setSlideRef, activeIndex, hasUserScrolled } =
    useFeedPaging({
      itemCount: items.length,
      initialIndex,
    });

  const { isMuted, toggleMute, activeSlideHasAudio, reportSlideAudio } =
    useFeedAudio({ activeIndex });

  const { isAuthorized } = useAuth();

  const wasHintSeenBeforeMountRef = useRef(hasSeenFeedScrollHint);
  const isScrollHintVisible =
    !wasHintSeenBeforeMountRef.current &&
    !hasUserScrolled &&
    activeIndex === initialIndex &&
    initialIndex < items.length - 1;

  useEffect(() => {
    if (isScrollHintVisible) markFeedScrollHintSeen();
  }, [isScrollHintVisible, markFeedScrollHintSeen]);

  const activeSlug = items[activeIndex]?.slug;
  useEffect(() => {
    if (activeSlug) {
      loadActivePreset(activeSlug).catch((error) =>
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
      );
    }
  }, [activeSlug, loadActivePreset]);

  usePresetFeedAnalytics({ activeIndex, hasUserScrolled, activeSlug });

  // Home stays mounted behind this overlay, hidden via `visibility` to keep its
  // scroll position — which leaves its cards intersecting, so their videos keep
  // decoding until the feed claims priority.
  useEffect(() => {
    activatePriorityVideo();
    return deactivatePriorityVideo;
  }, []);

  useEffect(() => () => setPresetFeedEntrySlug(null), [setPresetFeedEntrySlug]);

  const handleBack = useCallback(() => {
    // Remember the preset the user scrolled to so the "See all" grid can
    // silently land on it when we return.
    if (activeSlug) setFeedReturnSlug(activeSlug);
    setScreen(Screen.Library);
  }, [activeSlug, setFeedReturnSlug, setScreen]);

  const handleCloseApp = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CANCEL,
      screen_type: ScreenType.PRESET_FEED,
    });
    closeMiniapp().catch(() => {});
  }, []);

  const shouldHideBackButton = useHideBackButton();

  const isFeedEmpty = !isLoading && !items.length;

  useLayoutEffect(() => {
    if (isFeedEmpty) setScreen(Screen.Library);
  }, [isFeedEmpty, setScreen]);

  if (isFeedEmpty) {
    return null;
  }

  return (
    <Overlay
      className={classes.overlay}
      coverage="global"
      closeCallback={shouldHideBackButton ? handleCloseApp : handleBack}
      aria-label="Preset feed"
    >
      <div className={classes.shell}>
        {shouldHideBackButton ? (
          <InlineButton
            size="large"
            centerIcon={IconCross}
            className={classNames(classes.closeButton, classes.iconButton)}
            isWithoutBorder
            isWithoutBackground
            onClick={handleCloseApp}
            aria-label="Close app"
            dataTestId="preset-feed-close"
          />
        ) : (
          <InlineButton
            size="large"
            centerIcon={IconArrowLeft}
            className={classNames(classes.backButton, classes.iconButton)}
            isWithoutBorder
            isWithoutBackground
            onClick={handleBack}
            aria-label="Back"
            dataTestId="preset-feed-back"
          />
        )}

        {activeSlideHasAudio && (
          <InlineButton
            size="large"
            centerIcon={isMuted ? IconSoundOff : IconSoundOn}
            className={classNames(classes.muteButton, classes.iconButton)}
            isWithoutBorder
            isWithoutBackground
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            dataTestId="preset-feed-mute"
          />
        )}

        {isLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : (
          <FeedSlideList
            items={items}
            activeIndex={activeIndex}
            scrollRef={scrollRef}
            setSlideRef={setSlideRef}
            isMuted={isMuted}
            onAudioDetected={reportSlideAudio}
            onUploadTap={handleUploadTap}
            onEditTap={handleEditTap}
            isUploadLoading={isUploadLoading}
            shouldLoadCredits={isAuthorized}
          />
        )}

        <FeedInputsSheet
          isOpen={isInputsSheetOpen}
          isGenerating={isGenerating}
          onGenerate={handleSheetGenerate}
          onClose={closeSheet}
        />

        {isScrollHintVisible && (
          <div className={classes.scrollHintSlot}>
            <FeedScrollHint />
          </div>
        )}

        <input
          ref={filePickerInputProps.inputRef}
          type="file"
          accept={filePickerInputProps.accept}
          className={classes.hiddenFileInput}
          onChange={filePickerInputProps.onChange}
        />
      </div>
    </Overlay>
  );
};

export default PresetFeedScreen;
